import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { SelfHostedInstance } from "../../types/cloud";
import { StatusBadge } from "./StatusBadge";
import { ConfirmDialog } from "./ConfirmDialog";
import {
  deleteSelfHostedInstance,
  checkSelfHostedHealth,
} from "../../lib/api/cloud";

interface SelfHostedInstanceCardProps {
  instance: SelfHostedInstance;
  onRefresh: () => void;
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function SelfHostedInstanceCard({ instance, onRefresh }: SelfHostedInstanceCardProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [healthChecking, setHealthChecking] = useState(false);
  const [healthResult, setHealthResult] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteSelfHostedInstance(instance.id);
      onRefresh();
    } catch {
      // Error handling is simplified for now
    } finally {
      setLoading(false);
      setConfirmDelete(false);
    }
  };

  const handleHealthCheck = async () => {
    setHealthChecking(true);
    setHealthResult(null);
    try {
      const result = await checkSelfHostedHealth(instance.id);
      if (result.reachable) {
        setHealthResult("reachable");
      } else {
        setHealthResult(result.reason ?? "unreachable");
      }
      onRefresh();
    } catch {
      setHealthResult("check_failed");
    } finally {
      setHealthChecking(false);
    }
  };

  return (
    <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-[#f5f5f5]">
            {instance.subdomain}.cc.somebugs.com
          </h2>
          <p className="text-[#999] text-sm mt-0.5">
            {t('dashboard.selfHosted.instanceLabel')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded font-medium ${
            instance.mode === "public-ip"
              ? "bg-blue-500/10 text-blue-400"
              : "bg-purple-500/10 text-purple-400"
          }`}>
            {instance.mode === "public-ip"
              ? t('dashboard.selfHosted.mode.publicIp')
              : t('dashboard.selfHosted.mode.tunneled')}
          </span>
          <StatusBadge status={instance.status} size="md" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        {instance.mode === "public-ip" && instance.targetIp && (
          <div>
            <span className="text-[#666] text-xs uppercase tracking-wider">
              {t('dashboard.selfHosted.targetIp')}
            </span>
            <p className="text-[#f5f5f5] text-sm font-mono">{instance.targetIp}</p>
          </div>
        )}
        {instance.mode === "tunneled" && instance.tunnelPort && (
          <div>
            <span className="text-[#666] text-xs uppercase tracking-wider">
              {t('dashboard.selfHosted.tunnelPort')}
            </span>
            <p className="text-[#f5f5f5] text-sm font-mono">{instance.tunnelPort}</p>
          </div>
        )}
        {instance.lastPingAt && (
          <div>
            <span className="text-[#666] text-xs uppercase tracking-wider">
              {t('dashboard.selfHosted.lastPing')}
            </span>
            <p className="text-[#f5f5f5] text-sm">{formatRelativeTime(instance.lastPingAt)}</p>
          </div>
        )}
        <div>
          <span className="text-[#666] text-xs uppercase tracking-wider">
            {t('dashboard.instance.created')}
          </span>
          <p className="text-[#f5f5f5] text-sm">{formatRelativeTime(instance.createdAt)}</p>
        </div>
      </div>

      {instance.errorMessage && (
        <div className="mb-4 p-3 bg-red-500/5 border border-red-500/20 rounded text-red-400 text-sm">
          {instance.errorMessage}
        </div>
      )}

      {healthResult && (
        <div className={`mb-4 p-3 border rounded text-sm ${
          healthResult === "reachable"
            ? "bg-green-500/5 border-green-500/20 text-green-400"
            : "bg-yellow-500/5 border-yellow-500/20 text-yellow-400"
        }`}>
          {healthResult === "reachable"
            ? t('dashboard.selfHosted.healthReachable')
            : healthResult === "check_failed"
              ? t('dashboard.selfHosted.healthCheckFailed')
              : `${t('dashboard.selfHosted.healthUnreachable')}: ${healthResult}`}
        </div>
      )}

      <div className="flex gap-3 pt-2 border-t border-[#333]">
        {instance.status === "active" && (
          <>
            <a
              href={instance.url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-500/20 transition-colors"
            >
              {t('dashboard.instance.openCloseCode ADE')}
            </a>
            <button
              onClick={handleHealthCheck}
              disabled={healthChecking || loading}
              className="px-4 py-2 bg-green-500/10 text-green-400 rounded-lg text-sm font-medium hover:bg-green-500/20 transition-colors disabled:opacity-40"
            >
              {healthChecking ? t('dashboard.selfHosted.checking') : t('dashboard.selfHosted.testHealth')}
            </button>
          </>
        )}
        {(instance.status === "pending" || instance.status === "connecting") && (
          <button
            onClick={handleHealthCheck}
            disabled={healthChecking || loading}
            className="px-4 py-2 bg-green-500/10 text-green-400 rounded-lg text-sm font-medium hover:bg-green-500/20 transition-colors disabled:opacity-40"
          >
            {healthChecking ? t('dashboard.selfHosted.checking') : t('dashboard.selfHosted.testHealth')}
          </button>
        )}
        <button
          onClick={() => setConfirmDelete(true)}
          disabled={loading}
          className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/20 transition-colors disabled:opacity-40 ml-auto"
        >
          {t('common.delete')}
        </button>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title={t('dashboard.selfHosted.removeTitle')}
        message={t('dashboard.selfHosted.removeMessage')}
        confirmText={t('common.delete')}
        danger
        confirmInput={instance.subdomain}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
